/**
 * Confirm Action Modal - Shared confirmation dialog for destructive actions
 */
import type { ReactElement, ReactNode } from 'react';

import Modal from '@/components/Modal/Modal';

export interface ConfirmActionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Primary description text */
  description: ReactNode;
  /** Optional secondary text (smaller, muted) */
  secondaryText?: ReactNode;
  /** Confirm button label */
  confirmLabel: string;
  /** Confirm button label while pending */
  pendingLabel?: string;
  /** Whether the action is pending */
  isPending?: boolean;
  /** Handler for confirm action */
  onConfirm: () => void;
  /** Cancel button label */
  cancelLabel?: string;
}

export default function ConfirmActionModal({
  isOpen,
  onClose,
  title,
  description,
  secondaryText,
  confirmLabel,
  pendingLabel,
  isPending = false,
  onConfirm,
  cancelLabel = 'Cancel',
}: ConfirmActionModalProps): ReactElement {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        <p className="text-silver text-sm">{description}</p>
        {secondaryText ? <p className="text-ash text-xs">{secondaryText}</p> : null}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="text-silver hover:text-pearl rounded-lg px-4 py-2 text-sm transition-colors"
            disabled={isPending}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className="bg-danger hover:bg-danger/80 text-pearl rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            disabled={isPending}
          >
            {isPending ? (pendingLabel ?? confirmLabel) : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
